// src/auth/useAuth.js

// ---- Config ----
const KEY = "rcs_auth";          // unified key (used everywhere)
const OLD_KEYS = ["auth"];       // migrate from these if found

// ---- Helpers ----
const b64urlToJson = (b64url) => {
  try {
    const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const parseJwt = (token) => {
  if (!token || token.split(".").length !== 3) return null;
  const [, payload] = token.split(".");
  return b64urlToJson(payload);
};

// ---- Storage primitives ----
function readRaw() {
  // migrate old keys if present
  for (const k of OLD_KEYS) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) {
      try {
        // move to the new key in the same bucket
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
        // default to localStorage on migration
        localStorage.setItem(KEY, v);
      } catch {}
      break;
    }
  }
  // prefer localStorage (remembered) over sessionStorage
  return localStorage.getItem(KEY) || sessionStorage.getItem(KEY);
}

function writeRaw(data, remember) {
  // clear both to avoid stale shadowing (session beating local)
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY);
  const bucket = remember ? localStorage : sessionStorage;
  bucket.setItem(KEY, JSON.stringify(data || {}));
}

function clearRaw() {
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY);
  for (const k of OLD_KEYS) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }
}

// ---- Public API ----
export function getStoredAuth() {
  const raw = readRaw();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setStoredAuth(auth, remember = true) {
  writeRaw(auth, remember);
}

export function clearStoredAuth() {
  clearRaw();
}

export function getAccessToken() {
  return getStoredAuth()?.access_token || null;
}

export function getUser() {
  return getStoredAuth()?.user || null;
}

// Checks token presence AND client-side expiry
export function isAuthed() {
  // 👇 Skip normal auth check if we're on the password change screen
  const pathname = window.location.pathname;
  if (pathname === "/change-password" && sessionStorage.getItem("reset_token")) {
    return true; // allow password reset route to load
  }

  const token = getAccessToken();
  if (!token) return false;

  const payload = parseJwt(token);

  // If this is a password-reset scoped token, treat as unauthenticated elsewhere
  if (payload?.scope === "password_reset") return false;

  // Normal expiry check
  if (!payload?.exp) return true; // if no exp, let server decide
  const nowSec = Math.floor(Date.now() / 1000);
  if (payload.exp <= nowSec) {
    clearStoredAuth();
    return false;
  }

  return true;
}


// Keep tabs/windows in sync
export function listenAuthChanges(callback) {
  const handler = () => callback?.(getStoredAuth());
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
